import { Alert, AlertTitle, Box, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useEffect } from "react"

import { useToast } from "@crm-src/hooks/use-toast"

/**
 * Toast container — the app's real-time in-app snackbar surface (MUI
 * `Alert`s). Rebuilt from a single MUI `Snackbar` (which only ever shows
 * ONE toast at a fixed spot — a burst of events overlapped invisibly) into
 * a fixed bottom-right COLUMN that stacks several at once, each
 * severity-coloured and auto-dismissing on its own timer. This is how a
 * user "sees in real time what's happening" — new ticket / lead / SLA /
 * server-down events stream in here.
 *
 * The imperative `toast()` / `useToast()` API (the single shared store in
 * `@crm-src/hooks/use-toast`, which the dashboard re-exports via
 * `components/ui/use-toast`) is unchanged, so every call site keeps working.
 * Each store toast carries `{ title, description, action, variant, severity,
 * duration, open, onOpenChange }`.
 */
const DEFAULT_DURATION_MS = 6000

type StoreToast = ReturnType<typeof useToast>["toasts"][number]

function ToastItem({ toast }: { toast: StoreToast }) {
  const open = toast.open ?? true

  // Each toast auto-dismisses on its OWN timer (the old single Snackbar
  // owned the only timer). The store object's identity is stable until it
  // is updated/dismissed, so the timer isn't reset by sibling toasts
  // arriving.
  useEffect(() => {
    if (!open) return
    const id = setTimeout(
      () => toast.onOpenChange?.(false),
      toast.duration ?? DEFAULT_DURATION_MS,
    )
    return () => clearTimeout(id)
  }, [toast, open])

  if (!open) return null

  // Notifications set `severity` directly; legacy callers used the shadcn
  // `variant` ("destructive" → red error).
  const severity = toast.severity ?? (toast.variant === "destructive" ? "error" : "info")
  const close = () => toast.onOpenChange?.(false)

  return (
    <Alert
      variant="filled"
      severity={severity}
      sx={{
        width: "100%",
        alignItems: "flex-start",
        boxShadow: 6,
        "@keyframes xopToastIn": {
          from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
          to: { opacity: 1, transform: "none" },
        },
        animation: "xopToastIn 180ms ease-out",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {toast.action}
          <IconButton
            size="small"
            color="inherit"
            aria-label="Close"
            data-testid="toast-close"
            onClick={close}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      }
    >
      {toast.title && (
        <AlertTitle sx={{ mb: toast.description ? 0.5 : 0 }}>{toast.title}</AlertTitle>
      )}
      {toast.description}
    </Alert>
  )
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <Box
      data-testid="toaster"
      sx={{
        position: "fixed",
        bottom: 16,
        // In-app snackbars live in the BOTTOM-LEFT corner. (The desktop
        // app's separate OS-level notification popup window is bottom-right
        // — keeping the two on opposite corners stops them clashing.)
        left: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "min(420px, calc(100vw - 32px))",
        // The container spans a column but only the toasts should catch
        // clicks — let everything in the gaps fall through.
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </Box>
  )
}
